import React from 'react';
import { 
  BookmarkPlus, 
  MessageSquare, 
  Copy, 
  Share2, 
  Image as ImageIcon, 
  GitCompare, 
  Highlighter, 
  Volume2,
  Sparkles,
  Send,
  X 
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Mobile-optimized verse action menu
 * Shows horizontal icon bar at bottom, keeping verse visible
 */
const VerseActionMenuMobile = ({ 
  verse,
  onClose,
  onSave,
  onNote,
  onCopy,
  onShare,
  onImage,
  onCompare,
  onHighlight,
  onAudio,
  onPost,
  selectionCount = 1
}) => {
  const { language } = useLanguage();

  if (!verse) return null;

  const parseRef = (ref) => {
    if (!ref) return null;
    const m = String(ref).match(/^(.+)\s+(\d+):(\d+)/);
    if (!m) return null;
    return { book: m[1].trim(), chapter: parseInt(m[2], 10), verse: parseInt(m[3], 10) };
  };

  const handleVerseLens = () => {
    const parsed = parseRef(verse.reference);
    if (!parsed) return;
    window.dispatchEvent(new CustomEvent('lumera:verse-lens', { detail: parsed }));
    onClose();
  };

  const actions = [
    { id: 'lens', icon: Sparkles, onClick: handleVerseLens, label: language === 'es' ? 'Lente' : 'Lens', color: 'text-indigo-500' },
    { id: 'save', icon: BookmarkPlus, onClick: onSave, label: language === 'es' ? 'Guardar' : 'Save', color: 'text-blue-500' },
    { id: 'note', icon: MessageSquare, onClick: onNote, label: language === 'es' ? 'Nota' : 'Note', color: 'text-green-500' },
    { id: 'copy', icon: Copy, onClick: onCopy, label: language === 'es' ? 'Copiar' : 'Copy', color: 'text-slate-500' },
    { id: 'share', icon: Share2, onClick: onShare, label: language === 'es' ? 'Compartir' : 'Share', color: 'text-indigo-500' },
    { id: 'post', icon: Send, onClick: onPost, label: language === 'es' ? 'Publicar' : 'Post', color: 'text-fuchsia-500' },
    { id: 'image', icon: ImageIcon, onClick: onImage, label: language === 'es' ? 'Imagen' : 'Image', color: 'text-purple-500' },
    { id: 'compare', icon: GitCompare, onClick: onCompare, label: language === 'es' ? 'Comparar' : 'Compare', color: 'text-cyan-500' },
    { id: 'highlight', icon: Highlighter, onClick: onHighlight, label: language === 'es' ? 'Resaltar' : 'Highlight', color: 'text-yellow-500' },
    { id: 'audio', icon: Volume2, onClick: onAudio, label: language === 'es' ? 'Escuchar' : 'Listen', color: 'text-orange-500' }
  ];

  return (
    <>
      {/* Dim overlay — purely visual. pointer-events-none so taps pass through to
          other verses (enables multi-verse selection) and never auto-close the bar. */}
      <div 
        className="fixed inset-0 bg-black/20 z-[60] pointer-events-none" 
        aria-hidden="true"
      />
      
      {/* Action Bar at Bottom — above backdrop and all floating buttons */}
      <div className="fixed bottom-0 left-0 right-0 z-[70] bg-white dark:bg-slate-900 border-t-2 border-slate-200 dark:border-slate-700 shadow-2xl">
        {/* Verse Reference Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate flex-1" data-testid="verse-action-mobile-ref">
            {selectionCount > 1
              ? (language === 'es' ? `${selectionCount} versículos seleccionados` : `${selectionCount} verses selected`)
              : verse.reference}
          </p>
          <button
            onClick={onClose}
            className="ml-2 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Action icons — wrapped into rows so all stay visible on small phones */}
        <div className="grid grid-cols-5 gap-1 px-2 py-3">
          {actions.map((action) => (
            <button
              key={action.id}
              onClick={action.onClick}
              data-testid={`verse-action-mobile-${action.id}`}
              className="flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors active:scale-95"
            >
              <action.icon className={`w-6 h-6 ${action.color}`} />
              <span className="text-[10px] font-medium text-slate-700 dark:text-slate-300 text-center leading-tight">
                {action.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default VerseActionMenuMobile;
